/**
 * ForgotPasswordPage - Password Recovery
 *
 * Allows users to request a password reset via BetterAuth.
 * Styled to match LoginPage (background image, frosted glass card).
 */

import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { HiEnvelope, HiArrowLeft } from 'react-icons/hi2';
import { Input } from '../../components/ui/Input';
import { betterAuthForgotPassword } from '../../services/auth';
import { logger } from '../../../utils/logger';
import voicebrainLogo from '../../../assets/images/voicebrain-logo.svg';
import loginBackground from '../../../assets/images/login-background.jpg';

const BRAND_NAME = import.meta.env.VITE_BRAND_NAME || 'VoiceBrain';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const result = await betterAuthForgotPassword(email);
      if (result.error) {
        setError(result.error);
        setIsLoading(false);
        return;
      }
      setSubmitted(true);
      setIsLoading(false);
    } catch (err) {
      logger.error('[ForgotPasswordPage] Error:', err);
      setError('Failed to initiate password recovery. Please try again.');
      setIsLoading(false);
    }
  };

  if (submitted) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-4 relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${loginBackground})` }}
      >
        <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" aria-hidden="true" />
        <div className="w-full max-w-sm relative z-10">
          <div
            className="bg-vb-surface/90 dark:bg-vb-surface-dark/90 backdrop-blur-md rounded-xl shadow-2xl border border-vb-border/20 dark:border-vb-border-dark/50 p-8 text-center"
            role="status"
            aria-live="polite"
          >
            <div className="w-16 h-16 bg-vb-success/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <HiEnvelope className="w-8 h-8 text-vb-success" aria-hidden="true" />
            </div>
            <h1 className="text-xl font-bold text-vb-text-primary dark:text-vb-text-primary-dark mb-2">
              Check your email
            </h1>
            <p className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark mb-6">
              We&apos;ve sent password reset instructions to <strong>{email}</strong>
            </p>
            <Link
              to="/v2/login"
              className="inline-flex items-center gap-2 text-sm text-vb-primary hover:text-vb-primary-hover transition-colors"
            >
              <HiArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 relative bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${loginBackground})` }}
      role="main"
      aria-label={`${BRAND_NAME} password recovery`}
    >
      <div className="absolute inset-0 bg-black/50 backdrop-blur-[2px]" aria-hidden="true" />

      <div className="w-full max-w-sm relative z-10">
        <div
          className="bg-vb-surface/90 dark:bg-vb-surface-dark/90 backdrop-blur-md rounded-xl shadow-2xl border border-vb-border/20 dark:border-vb-border-dark/50 p-8"
          role="form"
          aria-labelledby="reset-heading"
        >
          {/* Header */}
          <div className="text-center mb-6">
            <img
              src={voicebrainLogo}
              alt={BRAND_NAME}
              className="h-10 mx-auto mb-3 invert dark:invert-0"
            />
            <h1
              id="reset-heading"
              className="text-lg font-semibold text-vb-text-primary dark:text-vb-text-primary-dark"
            >
              Reset your password
            </h1>
            <p className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark mt-1">
              Enter your email and we&apos;ll send you instructions
            </p>
          </div>

          {/* Error */}
          {error && (
            <div
              className="mb-4 p-3 rounded-lg bg-vb-danger/10 border border-vb-danger text-sm text-vb-danger"
              role="alert"
            >
              {error}
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4" aria-label="Password recovery form">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-vb-text-primary dark:text-vb-text-primary-dark mb-1"
              >
                Email address
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                autoComplete="email"
                autoFocus
                aria-required="true"
                placeholder="you@example.com"
              />
            </div>

            <button
              type="submit"
              className="w-full px-4 py-2 rounded-lg bg-vb-primary hover:bg-vb-primary-hover text-white font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isLoading || !email}
              aria-busy={isLoading}
            >
              {isLoading ? 'Sending...' : 'Send reset instructions'}
            </button>
          </form>

          {/* Back to login */}
          <div className="mt-6 text-center">
            <Link
              to="/v2/login"
              className="inline-flex items-center gap-2 text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark hover:text-vb-primary transition-colors"
            >
              <HiArrowLeft className="w-4 h-4" aria-hidden="true" />
              Back to sign in
            </Link>
          </div>
        </div>

        {/* Copyright */}
        <p className="mt-6 text-center text-xs text-vb-text-tertiary/50 dark:text-vb-text-tertiary-dark/50">
          &copy; {new Date().getFullYear()} {BRAND_NAME}
        </p>
      </div>
    </div>
  );
}

export default ForgotPasswordPage;
